import React, { useState, useCallback, useRef } from 'react';
import { Upload, Loader2, AlertCircle, FolderOpen, Layers, Image } from 'lucide-react';
import ExifReader from 'exifreader';
import { UploadGroup, FileWithExif, HDRStack, ExifData } from '../types';

interface SmartUploaderProps {
  onFilesAnalyzed: (groups: UploadGroup[]) => void;
}

// Parse EXIF date format: "2024:01:15 14:30:45" or object with description
const parseExifDate = (value: any): Date | undefined => {
  if (!value) return undefined;
  
  // Handle exifreader's format (object with description)
  const dateString = typeof value === 'object' ? value.description || value.value : value;
  if (!dateString || typeof dateString !== 'string') return undefined;
  
  try {
    // Handle EXIF format: "YYYY:MM:DD HH:MM:SS"
    const [datePart, timePart] = dateString.split(' ');
    if (!datePart) return undefined;
    
    const [year, month, day] = datePart.split(':').map(Number);
    const [hour = 0, minute = 0, second = 0] = (timePart?.split(':') || []).map(Number);
    
    return new Date(year, month - 1, day, hour, minute, second);
  } catch {
    return undefined;
  }
};

// Read EXIF data from a file using exifreader library
const readExifData = async (file: File): Promise<ExifData> => {
  try {
    // Skip very small files (likely corrupted)
    if (file.size < 1000) {
      console.warn(`[EXIF] Skipping small file: ${file.name} (${file.size} bytes)`);
      return { dateTimeOriginal: new Date(file.lastModified) };
    }
    
    const arrayBuffer = await file.arrayBuffer();
    const tags = ExifReader.load(arrayBuffer, { expanded: true });
    
    const exifData: ExifData = {};
    
    // Get capture time - try multiple EXIF fields
    const dateTimeOriginal = 
      tags.exif?.DateTimeOriginal || 
      tags.exif?.DateTimeDigitized || 
      tags.exif?.DateTime;
    
    if (dateTimeOriginal) {
      exifData.dateTimeOriginal = parseExifDate(dateTimeOriginal);
    }
    
    // Fallback to file's lastModified if no EXIF date
    if (!exifData.dateTimeOriginal) {
      console.log(`[EXIF] No EXIF date for ${file.name}, using lastModified`);
      exifData.dateTimeOriginal = new Date(file.lastModified);
    }
    
    // Get image dimensions
    if (tags.file?.['Image Width']) {
      exifData.imageWidth = tags.file['Image Width'].value;
    }
    if (tags.file?.['Image Height']) {
      exifData.imageHeight = tags.file['Image Height'].value;
    }
    
    // Get exposure info
    if (tags.exif?.ExposureBiasValue) {
      exifData.exposureBiasValue = tags.exif.ExposureBiasValue.value;
    }
    if (tags.exif?.ISOSpeedRatings) {
      exifData.iso = tags.exif.ISOSpeedRatings.value;
    }
    if (tags.exif?.FNumber) {
      exifData.fNumber = tags.exif.FNumber.value;
    }
    if (tags.exif?.ExposureTime) {
      exifData.exposureTime = tags.exif.ExposureTime.value;
    }
    if (tags.exif?.Model) {
      exifData.model = tags.exif.Model.description;
    }
    
    return exifData;
  } catch (error) {
    console.error(`[EXIF] Error reading ${file.name}:`, error);
    // Fallback - return basic data using file's lastModified
    return {
      dateTimeOriginal: new Date(file.lastModified),
    };
  }
};

// Group files by capture time proximity (within threshold = HDR stack)
const groupIntoStacks = (files: FileWithExif[]): { stacks: HDRStack[]; singles: FileWithExif[] } => {
  // Settings - configurable thresholds
  const STACK_THRESHOLD_MS = 2000; // 2 seconds - typical HDR brackets are shot within 1-2 seconds
  const MIN_STACK_SIZE = 2; // Allow 2-image stacks (bracket with 2 exposures)
  const MAX_STACK_SIZE = 9; // Allow up to 9 images per stack (most cameras max at 9)

  console.log(`[HDR Stacking] Starting with ${files.length} files`);

  if (files.length === 0) {
    return { stacks: [], singles: [] };
  }

  // Sort by capture time
  const sorted = [...files].sort((a, b) => {
    const timeA = a.exif.dateTimeOriginal?.getTime() || 0;
    const timeB = b.exif.dateTimeOriginal?.getTime() || 0;
    return timeA - timeB;
  });

  // Log sorted times for debugging
  console.log('[HDR Stacking] Sorted capture times:');
  sorted.forEach((f, i) => {
    const time = f.exif.dateTimeOriginal;
    console.log(`  ${i}: ${f.file.name} @ ${time ? time.toISOString() : 'NO DATE'}`);
  });

  const stacks: HDRStack[] = [];
  const singles: FileWithExif[] = [];
  
  let currentStack: FileWithExif[] = [];
  let lastTime: number | null = null;

  const finalizeStack = () => {
    if (currentStack.length === 0) return;
    
    if (currentStack.length >= MIN_STACK_SIZE && currentStack.length <= MAX_STACK_SIZE) {
      stacks.push({
        id: crypto.randomUUID(),
        files: [...currentStack],
        captureTimeStart: currentStack[0].exif.dateTimeOriginal || new Date(),
      });
      console.log(`[HDR Stacking] Created stack with ${currentStack.length} files`);
    } else if (currentStack.length > MAX_STACK_SIZE) {
      // Too many files - split into multiple stacks of reasonable size
      console.log(`[HDR Stacking] Stack too large (${currentStack.length}), splitting...`);
      const chunkSize = 5; // Split into chunks of 5
      for (let i = 0; i < currentStack.length; i += chunkSize) {
        const chunk = currentStack.slice(i, i + chunkSize);
        if (chunk.length >= MIN_STACK_SIZE) {
          stacks.push({
            id: crypto.randomUUID(),
            files: chunk,
            captureTimeStart: chunk[0].exif.dateTimeOriginal || new Date(),
          });
          console.log(`[HDR Stacking] Created split stack with ${chunk.length} files`);
        } else {
          singles.push(...chunk);
        }
      }
    } else {
      // Not enough files for a stack
      console.log(`[HDR Stacking] Stack size ${currentStack.length} < ${MIN_STACK_SIZE}, treating as singles`);
      singles.push(...currentStack);
    }
  };

  for (const file of sorted) {
    const currentTime = file.exif.dateTimeOriginal?.getTime() || 0;
    const timeDiff = lastTime !== null ? Math.abs(currentTime - lastTime) : 0;

    if (lastTime === null || timeDiff <= STACK_THRESHOLD_MS) {
      currentStack.push(file);
    } else {
      console.log(`[HDR Stacking] Time gap ${timeDiff}ms > ${STACK_THRESHOLD_MS}ms, finalizing stack of ${currentStack.length}`);
      finalizeStack();
      currentStack = [file];
    }
    
    lastTime = currentTime;
  }

  // Handle remaining files
  console.log(`[HDR Stacking] Finalizing last group of ${currentStack.length} files`);
  finalizeStack();

  console.log(`[HDR Stacking] Done: ${stacks.length} stacks, ${singles.length} singles`);
  
  // Validate result
  const totalFilesInStacks = stacks.reduce((acc, s) => acc + s.files.length, 0);
  const totalProcessed = totalFilesInStacks + singles.length;
  if (totalProcessed !== files.length) {
    console.error(`[HDR Stacking] WARNING: File count mismatch! Input: ${files.length}, Output: ${totalProcessed}`);
  }
  
  return { stacks, singles };
};

// Camera RAW files are TIFF containers, so a RAW extension wins over a TIFF content type.
const isTiffFile = (file: File): boolean => {
  if (/\.tiff?$/i.test(file.name)) return true;
  if (/\.(raw|cr2|cr3|nef|arw|dng|rw2|raf|orf|srw)$/i.test(file.name)) return false;
  return file.type.toLowerCase().includes('tiff');
};

// Extract folder name from file path for grouping
const extractFolderName = (file: File): string => {
  // webkitRelativePath contains the folder structure when using folder upload
  const path = (file as any).webkitRelativePath || file.name;
  const parts = path.split('/');
  
  // Return parent folder name if available
  if (parts.length > 1) {
    return parts[parts.length - 2];
  }
  
  return 'Untitled Listing';
};

const SmartUploader: React.FC<SmartUploaderProps> = ({ onFilesAnalyzed }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analyzeProgress, setAnalyzeProgress] = useState({ current: 0, total: 0 });
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const processFiles = useCallback(async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    console.log('[HDR Upload] Total files received:', fileArray.length);
    
    const tiffFiles = fileArray.filter(isTiffFile);
    const tiffError = tiffFiles.length
      ? `TIFF files are not accepted. Export JPEGs (or upload the camera RAW files) and try again. Skipped: ${tiffFiles
          .slice(0, 6)
          .map(f => f.name)
          .join(', ')}${tiffFiles.length > 6 ? '…' : ''}`
      : null;

    const imageFiles = fileArray.filter(f =>
      !isTiffFile(f) &&
      (f.type.startsWith('image/') || /\.(jpg|jpeg|png|raw|cr2|cr3|nef|arw|dng|rw2)$/i.test(f.name))
    );
    
    console.log('[HDR Upload] Image files after filter:', imageFiles.length);
    console.log('[HDR Upload] File names:', imageFiles.map(f => f.name));

    if (imageFiles.length === 0) {
      setError(tiffError ?? 'No valid image files found. Please upload JPG, PNG, or RAW files.');
      return;
    }

    setIsAnalyzing(true);
    setError(tiffError);
    setAnalyzeProgress({ current: 0, total: imageFiles.length });

    try {
      // Read EXIF data from all files
      const filesWithExif: FileWithExif[] = [];
      
      for (let i = 0; i < imageFiles.length; i++) {
        const file = imageFiles[i];
        const exif = await readExifData(file);
        
        console.log(`[HDR Upload] File ${i + 1}: ${file.name}, capture time: ${exif.dateTimeOriginal?.toISOString()}`);
        
        filesWithExif.push({
          file,
          exif,
          previewUrl: URL.createObjectURL(file),
        });
        
        setAnalyzeProgress({ current: i + 1, total: imageFiles.length });
      }

      // Group files by folder
      const folderGroups = new Map<string, FileWithExif[]>();
      
      for (const fileWithExif of filesWithExif) {
        const folderName = extractFolderName(fileWithExif.file);
        const existing = folderGroups.get(folderName) || [];
        folderGroups.set(folderName, [...existing, fileWithExif]);
      }
      
      console.log('[HDR Upload] Folder groups:', Array.from(folderGroups.keys()));

      // Process each folder into an UploadGroup
      const uploadGroups: UploadGroup[] = [];
      
      for (const [folderName, folderFiles] of folderGroups) {
        console.log(`[HDR Upload] Processing folder "${folderName}" with ${folderFiles.length} files`);
        const { stacks, singles } = groupIntoStacks(folderFiles);
        console.log(`[HDR Upload] Result: ${stacks.length} stacks, ${singles.length} singles`);
        
        uploadGroups.push({
          id: crypto.randomUUID(),
          name: folderName,
          stacks,
          singleImages: singles,
        });
      }

      console.log('[HDR Upload] Final upload groups:', uploadGroups.length);
      
      // Show detection summary for debugging
      const totalStacks = uploadGroups.reduce((acc, g) => acc + g.stacks.length, 0);
      const totalSingles = uploadGroups.reduce((acc, g) => acc + g.singleImages.length, 0);
      const totalFilesInStacks = uploadGroups.reduce((acc, g) => 
        acc + g.stacks.reduce((a, s) => a + s.files.length, 0), 0
      );
      
      // Detailed logging for debugging
      console.log('='.repeat(60));
      console.log('[HDR Upload] DETECTION SUMMARY');
      console.log('='.repeat(60));
      console.log(`[HDR Upload] Total listings: ${uploadGroups.length}`);
      console.log(`[HDR Upload] Total stacks: ${totalStacks}`);
      console.log(`[HDR Upload] Total files in stacks: ${totalFilesInStacks}`);
      console.log(`[HDR Upload] Total singles: ${totalSingles}`);
      
      // Log each stack's details
      uploadGroups.forEach((group, gi) => {
        console.log(`\n[HDR Upload] Listing ${gi + 1}: "${group.name}"`);
        group.stacks.forEach((stack, si) => {
          console.log(`  Stack ${si + 1}: ${stack.files.length} files`);
          stack.files.forEach((f, fi) => {
            const time = f.exif.dateTimeOriginal;
            console.log(`    ${fi + 1}. ${f.file.name} @ ${time ? time.toISOString() : 'NO DATE'}`);
          });
        });
        if (group.singleImages.length > 0) {
          console.log(`  Singles: ${group.singleImages.length} files`);
          group.singleImages.forEach((f, fi) => {
            console.log(`    ${fi + 1}. ${f.file.name}`);
          });
        }
      });
      console.log('='.repeat(60));
      
      const detectionSummary = `Files detected:\n\n${uploadGroups.length} listing(s)\n${totalStacks} HDR stack(s) with ${totalFilesInStacks} files\n${totalSingles} single image(s)${tiffError ? `\n\n${tiffError}` : ''}`;
      console.log('[HDR Upload]', detectionSummary);
      window.alert(detectionSummary);
      
      onFilesAnalyzed(uploadGroups);
    } catch (err) {
      console.error('Analysis error:', err);
      setError('Failed to analyze files. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  }, [onFilesAnalyzed]);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files: File[] = [];
    
    // IMPORTANT: Capture entries synchronously before dataTransfer is cleared
    const entries: FileSystemEntry[] = [];
    const items = e.dataTransfer.items;
    
    for (let i = 0; i < items.length; i++) {
      const entry = items[i].webkitGetAsEntry();
      if (entry) {
        entries.push(entry);
      }
    }
    
    console.log(`[Drop] Captured ${entries.length} entries from drop`);
    
    // Also capture files directly as fallback
    const directFiles = Array.from(e.dataTransfer.files);
    console.log(`[Drop] Direct files: ${directFiles.length}`);
    
    // Handle both files and folders
    const processEntry = async (entry: FileSystemEntry): Promise<void> => {
      if (entry.isFile) {
        const fileEntry = entry as FileSystemFileEntry;
        return new Promise((resolve) => {
          fileEntry.file((file) => {
            files.push(file);
            resolve();
          }, (err) => {
            console.error(`[Drop] Error reading file entry: ${err}`);
            resolve();
          });
        });
      } else if (entry.isDirectory) {
        const dirEntry = entry as FileSystemDirectoryEntry;
        const reader = dirEntry.createReader();
        
        // readEntries may need to be called multiple times
        const readAllEntries = async (): Promise<FileSystemEntry[]> => {
          const allEntries: FileSystemEntry[] = [];
          
          const readBatch = (): Promise<FileSystemEntry[]> => {
            return new Promise((resolve) => {
              reader.readEntries((batch) => {
                resolve(batch);
              }, (err) => {
                console.error(`[Drop] Error reading directory: ${err}`);
                resolve([]);
              });
            });
          };
          
          let batch = await readBatch();
          while (batch.length > 0) {
            allEntries.push(...batch);
            batch = await readBatch();
          }
          
          return allEntries;
        };
        
        const subEntries = await readAllEntries();
        console.log(`[Drop] Directory "${entry.name}" has ${subEntries.length} entries`);
        
        for (const subEntry of subEntries) {
          await processEntry(subEntry);
        }
      }
    };

    // Process all captured entries
    for (const entry of entries) {
      await processEntry(entry);
    }
    
    console.log(`[Drop] Processed ${files.length} files from entries`);
    
    // Fallback: if no files from entries, use direct files
    if (files.length === 0 && directFiles.length > 0) {
      console.log(`[Drop] Using fallback: ${directFiles.length} direct files`);
      processFiles(directFiles);
    } else if (files.length > 0) {
      processFiles(files);
    } else {
      console.warn('[Drop] No files found in drop');
    }
  }, [processFiles]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  return (
    <div className="space-y-4">
      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          relative border-2 border-dashed rounded-2xl p-12 text-center transition-all
          ${isDragging 
            ? 'border-brand-500 bg-brand-500/10' 
            : 'border-slate-700 hover:border-slate-600'
          }
          ${isAnalyzing ? 'pointer-events-none' : 'cursor-pointer'}
        `}
      >
        {isAnalyzing ? (
          <div className="flex flex-col items-center">
            <Loader2 className="w-12 h-12 text-brand-500 animate-spin mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">
              Analyzing photos...
            </h3>
            <p className="text-slate-400">
              {analyzeProgress.current} of {analyzeProgress.total} files
            </p>
            <div className="w-64 h-2 bg-slate-700 rounded-full mt-4 overflow-hidden">
              <div 
                className="h-full bg-brand-500 transition-all duration-300"
                style={{ 
                  width: `${analyzeProgress.total > 0 
                    ? (analyzeProgress.current / analyzeProgress.total) * 100 
                    : 0}%` 
                }}
              />
            </div>
          </div>
        ) : (
          <>
            <Upload className={`w-12 h-12 mx-auto mb-4 ${isDragging ? 'text-brand-400' : 'text-slate-500'}`} />
            <h3 className="text-lg font-semibold text-white mb-2">
              Drop your photos here
            </h3>
            <p className="text-slate-400 mb-6 max-w-md mx-auto">
              Drop a shoot folder or a set of brackets. We’ll group frames captured a couple of seconds apart into HDR stacks.
            </p>
            
            <div className="flex items-center justify-center gap-4">
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-4 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <Image className="w-5 h-5" />
                Select Files
              </button>
              <button
                onClick={() => folderInputRef.current?.click()}
                className="px-4 py-2.5 bg-brand-500 hover:bg-brand-600 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <FolderOpen className="w-5 h-5" />
                Select Folder
              </button>
            </div>
          </>
        )}

        {/* Hidden file inputs */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*,.raw,.cr2,.cr3,.nef,.arw,.dng,.rw2"
          onChange={(e) => e.target.files && processFiles(e.target.files)}
          className="hidden"
        />
        <input
          ref={folderInputRef}
          type="file"
          // @ts-ignore - webkitdirectory is not in React types
          webkitdirectory=""
          directory=""
          multiple
          onChange={(e) => e.target.files && processFiles(e.target.files)}
          className="hidden"
        />
      </div>

      {/* Error message */}
      {error && (
        <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {/* Info cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="p-4 bg-slate-800/50 border border-slate-700/50 rounded-xl">
          <div className="w-10 h-10 bg-indigo-500/20 rounded-lg flex items-center justify-center mb-3">
            <Layers className="w-5 h-5 text-indigo-400" />
          </div>
          <h4 className="font-medium text-white mb-1">Smart Stacking</h4>
          <p className="text-sm text-slate-400">
            3-5 photos within 2 seconds are grouped as HDR brackets
          </p>
        </div>
        
        <div className="p-4 bg-slate-800/50 border border-slate-700/50 rounded-xl">
          <div className="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center mb-3">
            <FolderOpen className="w-5 h-5 text-emerald-400" />
          </div>
          <h4 className="font-medium text-white mb-1">Folder Grouping</h4>
          <p className="text-sm text-slate-400">
            Photos from each folder become a separate listing
          </p>
        </div>
        
        <div className="p-4 bg-slate-800/50 border border-slate-700/50 rounded-xl">
          <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center mb-3">
            <Image className="w-5 h-5 text-amber-400" />
          </div>
          <h4 className="font-medium text-white mb-1">Format Support</h4>
          <p className="text-sm text-slate-400">
            JPEG, PNG and RAW files (CR2, CR3, NEF, ARW, DNG, RW2). TIFF is not accepted.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SmartUploader;
